import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import { RefinementList } from 'react-instantsearch';

import { useHasMounted } from '@hooks/useHasMounted/useHasMounted';

import ClothingSizeRefinement from '@components/algolia/SearchResults/Sidebar/ClothingSizeRefinement';
import ColourRefinement from '@components/algolia/SearchResults/Sidebar/ColourRefinement';
import PriceRefinement from '@components/algolia/SearchResults/Sidebar/PriceRefinement';
import RatingRefinement from '@components/algolia/SearchResults/Sidebar/RatingRefinement';
import ScrollableRefinementList from '@components/algolia/SearchResults/Sidebar/ScrollableRefinementList';
import SizeBucketRefinement from '@components/algolia/SearchResults/Sidebar/SizeBucketRefinement';
import CategoryNav from '@components/algolia/SearchResults/Sidebar/CategoryNav';
import CategoryNavV2 from '_components/Products/CategoryNav';
import ColourRefinementV2 from '_components/Products/ColourRefinement';
import PriceRefinementV2 from '_components/Products/PriceRefinement';
import ClothingSizeRefinementV2 from '_components/Products/ClothingSizeRefinement';
import SizeBucketRefinementV2 from '_components/Products/SizeBucketRefinement';
import RatingRefinementV2 from '_components/Products/RatingRefinement';
import { refinementListClassNames } from '@components/algolia/SearchResults/Sidebar/refinementListClassNames';
import { refinementListClassNamesV2 } from '@components/algolia/SearchResults/facets/refinementListClassNamesV2';
import facetStyles from './RefinementListV2.module.scss';
import { getFacetPluralLabel } from '@components/algolia/SearchResults/facets/getFacetTitle';
import { AlgoliaFacets } from '@utils/algolia/knownFacetNames';
import { isEffectivePriceAttribute } from '@utils/algolia/priceContext';
import { localizedProductsSlug } from '@utils/algolia/helpers';
import { FEATURE_FLAG_PLP_FILTERS_V2 } from '@config/feature-flag';

const FacetRefinement = ({
  attribute,
  locale,
  onRequestClose,
  context,
  hasActiveRefinement = false,
}: {
  attribute: string;
  locale?: string;
  onRequestClose?: () => void;
  context?: 'filterBar' | 'allFilters';
  /**
   * Whether this facet currently has an active refinement — pass the
   * caller's own `getRefinementCount(attribute) > 0` (e.g. from
   * AllFiltersPanel or FilterPill). Used to auto-expand a collapsed
   * scrollable list (Brand, or the generic facet) so a selected value
   * beyond the collapsed limit isn't hidden behind a "Show more" the
   * customer has to know to click. Not meaningful for facets that don't
   * render through ScrollableRefinementList.
   */
  hasActiveRefinement?: boolean;
}) => {
  const { t } = useTranslation(['searchResults']);
  const hasMounted = useHasMounted();

  const listClassNames = FEATURE_FLAG_PLP_FILTERS_V2
    ? refinementListClassNamesV2(context)
    : refinementListClassNames;

  const showMoreTranslationsFor = useMemo(
    () => (facetAttribute: string) => {
      const title = getFacetPluralLabel(facetAttribute, t);
      return {
        showMoreButtonText({ isShowingMore }: { isShowingMore: boolean }) {
          return isShowingMore
            ? t('search.showLessFacet', { title })
            : t('search.showAllFacet', { title });
        },
      };
    },
    [t]
  );

  if (attribute.startsWith('categories')) {
    const CategoryNavComponent = FEATURE_FLAG_PLP_FILTERS_V2
      ? CategoryNavV2
      : CategoryNav;
    return (
      <CategoryNavComponent
        limit={50}
        rootPath={localizedProductsSlug(locale)}
        locale={locale}
        attributes={[
          'categories.lvl0',
          'categories.lvl1',
          'categories.lvl2',
          'categories.lvl3',
          'categories.lvl4',
          'categories.lvl5',
          'categories.lvl6',
          'categories.lvl7',
        ]}
      />
    );
  }

  if (attribute === AlgoliaFacets.Gender) {
    return (
      <RefinementList
        attribute={AlgoliaFacets.Gender}
        limit={3}
        showMore={true}
        translations={showMoreTranslationsFor(AlgoliaFacets.Gender)}
        classNames={listClassNames}
      />
    );
  }

  if (attribute === AlgoliaFacets.Brand) {
    return (
      <div className={facetStyles.searchableFacet}>
        <ScrollableRefinementList autoExpand={hasActiveRefinement}>
          <RefinementList
            attribute={AlgoliaFacets.Brand}
            limit={6}
            showMore={true}
            translations={{
              ...showMoreTranslationsFor(AlgoliaFacets.Brand),
              noResultsText: t('search.facet.brand.noResults'),
            }}
            searchable={true}
            searchablePlaceholder={t('search.facet.brand.search')}
            classNames={listClassNames}
            showMoreLimit={1000}
            sortBy={['count:desc', 'name:asc']}
          />
        </ScrollableRefinementList>
      </div>
    );
  }

  if (
    attribute === AlgoliaFacets.Price ||
    isEffectivePriceAttribute(attribute)
  ) {
    return FEATURE_FLAG_PLP_FILTERS_V2 ? (
      <PriceRefinementV2 attribute={attribute} locale={locale} />
    ) : (
      <PriceRefinement attribute={attribute} />
    );
  }

  if (
    attribute === AlgoliaFacets.Colour ||
    attribute === AlgoliaFacets.ColourVariants
  ) {
    const ColourRefinementComponent = FEATURE_FLAG_PLP_FILTERS_V2
      ? ColourRefinementV2
      : ColourRefinement;
    return (
      <ColourRefinementComponent
        attribute={AlgoliaFacets.ColourVariants}
        locale={locale}
      />
    );
  }

  if (
    attribute === AlgoliaFacets.Rating ||
    attribute === AlgoliaFacets.ReviewScoreFlattened
  ) {
    const RatingRefinementComponent = FEATURE_FLAG_PLP_FILTERS_V2
      ? RatingRefinementV2
      : RatingRefinement;
    return (
      <RatingRefinementComponent
        attribute={AlgoliaFacets.ReviewScoreFlattened}
        locale={locale}
      />
    );
  }

  if (attribute === 'size') {
    if (!hasMounted) return null;
    return FEATURE_FLAG_PLP_FILTERS_V2 ? (
      <SizeBucketRefinementV2 attribute="size" locale={locale} />
    ) : (
      <SizeBucketRefinement
        attribute="size"
        locale={locale}
        onSizeSelected={() => onRequestClose?.()}
      />
    );
  }

  if (
    attribute === AlgoliaFacets.ClothingSizeKids ||
    attribute === AlgoliaFacets.ClothingSizeMens ||
    attribute === AlgoliaFacets.ClothingSizeWomens
  ) {
    return FEATURE_FLAG_PLP_FILTERS_V2 ? (
      <ClothingSizeRefinementV2 attribute={attribute} locale={locale} />
    ) : (
      <ScrollableRefinementList>
        <ClothingSizeRefinement attribute={attribute} locale={locale} />
      </ScrollableRefinementList>
    );
  }

  return (
    <ScrollableRefinementList autoExpand={hasActiveRefinement}>
      <RefinementList
        attribute={attribute}
        translations={showMoreTranslationsFor(attribute)}
        classNames={listClassNames}
        showMore={true}
        showMoreLimit={1000}
      />
    </ScrollableRefinementList>
  );
};

export default FacetRefinement;
